import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { EmailTemplatePreviewModal } from '@/features/campaigns/EmailCampaign/templates/EmailTemplatePreviewModal';
import { HtmlEditorPortal } from '@/features/campaigns/EmailCampaign/templates/HtmlEditorPortal';
import type { EmailTemplateFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Email/TemplatePreview/EmailTemplatePreview';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useTemplateImageUpload } from '@/features/campaigns/hooks/useTemplateImageUpload';
import { EMAIL_CONNECTOR_NAMES, useEmailConnectorFlowRunner } from '@/features/connectors/email';
import { hasMappingsUi } from '@/features/connectors/flows';
import { SMS_CONNECTOR_NAME, useSmsConnectorFlowRunner } from '@/features/connectors/sms';
import {
  toWhatsappInsertPrefill,
  useWhatsappChannelConnector,
  useWhatsappConnectorFlowRunner,
  WHATSAPP_CONNECTOR_NAMES,
} from '@/features/connectors/whatsapp';
import { showToast } from '@/features/toasts/toastsSlice';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { Button } from '@/lib';
import { ToastType } from '@/models/toast';
import HtmlEditor from '@/modules/HTML_EDITOR';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComuniTopic } from '../../constants';
import { ComuniChannel } from '../../templates/catalog';
import {
  applyLockedTopicToFlowParams,
  stripLockedTopicFromInitialFormData,
  stripLockedTopicFromNameFields,
  withLockedTopicNameStrip,
} from '../../templates/lockedTopicName';
import { stripTopicPrefix } from '../../templates/topicPrefix';
import { getEditFlowStepHandler, type EditFlowSelection, whatsappEditSelectionKey } from './editFlow';

type Phase = 'form' | 'design' | 'mappings';

/** Insert = create from catalog / blank; Edit / Delete = campaign-equivalent connector flows. */
export type TemplateFlowRole = 'templateInsert' | 'templateEdit' | 'templateDelete';

export const CHANNEL_CONNECTOR_NAMES: Record<ComuniChannel, string[]> = {
  whatsapp: [...WHATSAPP_CONNECTOR_NAMES],
  sms: [SMS_CONNECTOR_NAME],
  email: [...EMAIL_CONNECTOR_NAMES],
};

type TemplateFlowEditorProps = {
  channel: ComuniChannel;
  title: string;
  /**
   * Defaults to insert (create). Catalog Customize and + Create use templateInsert;
   * My templates / customs use templateEdit or templateDelete.
   */
  role?: TemplateFlowRole;
  /** Catalog prefill for templateInsert (SYSTEM_MODELS). Ignored for edit/delete. */
  initialFormData?: Record<string, FlowFormData>;
  /** Optional Edit Template step auto-fill (WhatsApp). */
  editSelection?: EditFlowSelection;
  /** When set (Customize or Edit from a topic group), the topic prefix is hidden and attached on submit. */
  lockedTopic?: ComuniTopic;
  onExit: (created: boolean) => void;
};

const successToastKey = (role: TemplateFlowRole): string => {
  if (role === 'templateEdit') return 'modelli.editor.editSuccess';
  if (role === 'templateDelete') return 'modelli.editor.deleteSuccess';
  return 'modelli.editor.createSuccess';
};

const useCreateTemplateHandler = (
  createInstance: (flowParams: FlowFormData, flowMappings: FlowFormData) => Promise<{ success: boolean; message?: string }>,
  onExit: (created: boolean) => void,
  role: TemplateFlowRole
) => {
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createTemplate = useCallback(
    async (flowParams: FlowFormData, flowMappings: FlowFormData) => {
      if (isSubmitting) return { success: false as const };
      setCreateError(null);
      setIsSubmitting(true);
      try {
        const result = await createInstance(flowParams, flowMappings);
        if (!result.success) {
          const message = result.message ?? String(tCampaign('flowInstanceCreationFailed'));
          setCreateError(message);
          appDispatch(showToast({ type: ToastType.ERROR, title: message }));
          return { success: false as const, message };
        }
        appDispatch(showToast({ title: String(t(successToastKey(role))), type: ToastType.SUCCESS }));
        appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
        onExit(true);
        return { success: true as const };
      } finally {
        setIsSubmitting(false);
      }
    },
    [appDispatch, createInstance, isSubmitting, onExit, role, t, tCampaign]
  );

  return { createTemplate, createError, isSubmitting };
};

const useLockedTopicSubmit = (lockedTopic: ComuniTopic | undefined) =>
  useCallback(
    (data: FlowFormData, suffix?: string) => (lockedTopic ? applyLockedTopicToFlowParams(data, lockedTopic, suffix) : data),
    [lockedTopic]
  );

const withStrippedPrefill = (
  prefill: Record<string, FlowFormData> | undefined,
  lockedTopic: ComuniTopic | undefined
): Record<string, FlowFormData> | undefined => {
  if (!prefill || !lockedTopic) return prefill;
  return stripLockedTopicFromInitialFormData(prefill, lockedTopic);
};

/** Keep name fields prefix-free in editor UI when a topic is locked; re-attach on submit via lockParams. */
const forEditorDisplay = (data: FlowFormData, lockedTopic: ComuniTopic | undefined): FlowFormData =>
  lockedTopic ? stripLockedTopicFromNameFields(data, lockedTopic) : data;

const WhatsappTemplateFlowEditor = ({
  title,
  role = 'templateInsert',
  initialFormData,
  editSelection,
  lockedTopic,
  onExit,
}: Omit<TemplateFlowEditorProps, 'channel'>) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');

  const {
    prepareResult,
    prepareError,
    isPreparePending,
    reloadPrepare,
    createInstance,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  } = useWhatsappConnectorFlowRunner(role, {
    audienceId,
    lang: currentLanguage,
  });

  // Catalog prefill is insert-only. Meta seeds step2; Business remaps onto step1 + Gupshup field ids.
  const { usesBusiness } = useWhatsappChannelConnector();
  const catalogPayload =
    role === 'templateInsert' ? (initialFormData?.['step2'] ?? initialFormData?.['step1']) : undefined;
  const rawPrefill = role === 'templateInsert' ? toWhatsappInsertPrefill(catalogPayload, usesBusiness) : undefined;
  const prefill = useMemo(() => withStrippedPrefill(rawPrefill, lockedTopic), [lockedTopic, rawPrefill]);

  const resolveStepAutoFill = useMemo(
    () =>
      withLockedTopicNameStrip(
        role === 'templateEdit' || role === 'templateDelete' ? getEditFlowStepHandler('whatsapp', editSelection) : undefined,
        lockedTopic
      ),
    [editSelection, lockedTopic, role]
  );
  const selectionKey =
    editSelection?.channel === 'whatsapp' ? whatsappEditSelectionKey(editSelection.template) : 'none';

  const { createTemplate, createError, isSubmitting } = useCreateTemplateHandler(createInstance, onExit, role);
  const lockParams = useLockedTopicSubmit(lockedTopic);

  const [phase, setPhase] = useState<Phase>('form');
  const [formData, setFormData] = useState<FlowFormData>({});
  const [mappingsConfig, setMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);

  const finishWithParams = (flowParams: FlowFormData, config: FlowMappingsConfig | null) => {
    const displayParams = forEditorDisplay(flowParams, lockedTopic);
    setFormData(displayParams);
    setMappingsConfig(config);
    setMappingData({});
    setIsMappingsFormValid(false);
    if (hasMappingsUi(config)) {
      setPhase('mappings');
      return;
    }
    void createTemplate(lockParams(displayParams), {});
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const mappingsConfirmButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void createTemplate(lockParams(formData), mappingData),
    disabled: !isMappingsFormValid || isSubmitting,
  };
  const mappingsCancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => onExit(false),
  };

  const isConnectorReady = !!connectorInstanceId && !!connectorFlowId;
  const isEditorLoading = isAudienceLoading || isChannelLoading || (hasAudience && isPreparePending);
  const connectorUnavailable = !isChannelLoading && !isConnectorReady;

  return (
    <div className='space-y-4'>
      <button type='button' onClick={() => onExit(false)} className='text-sm font-semibold text-brand-700 hover:underline'>
        ← {t('modelli.editor.backToList')}
      </button>
      <h2 className='text-2xl font-bold text-primary-900'>{title}</h2>
      {createError ? <div className='rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>{createError}</div> : null}

      {isAudienceLoading ? (
        <Loading />
      ) : isAudienceSettled && !hasAudience ? (
        <AudienceRequiredEmptyState />
      ) : connectorUnavailable ? (
        <p className='text-sm text-tertiary-600'>{t('modelli.editor.connectorUnavailable')}</p>
      ) : (
        <div className={phase === 'form' ? undefined : 'hidden'}>
          <ErrorBoundary>
            {prepareResult ? (
              <MultiStepFlowForm
                key={`${connectorFlowId}-${audienceId}-${usesBusiness ? 'biz' : 'meta'}-${selectionKey}`}
                id='comuni-template-flow-whatsapp'
                showTemplatePreview
                templatePreviewVariant='whatsapp'
                audienceId={audienceId!}
                connectorInstanceId={connectorInstanceId!}
                connectorFlowId={connectorFlowId!}
                paramsConfig={prepareResult}
                initialFormData={prefill}
                resolveStepAutoFill={resolveStepAutoFill}
                isInAction={isSubmitting}
                onComplete={(allParams, config) => finishWithParams(allParams, config)}
              />
            ) : (
              <div className='flex flex-col items-start gap-3'>
                <p className='text-sm text-tertiary-600'>
                  {isEditorLoading && !prepareError
                    ? tCampaign('smsWorkspace.templates.templateLoadingMessage')
                    : tCampaign('smsWorkspace.templates.failedToLoadTemplate')}
                </p>
                {prepareError ? (
                  <Button intent='secondary' onClick={() => reloadPrepare()} text={tCampaign('smsWorkspace.templates.tryAgain')} />
                ) : null}
              </div>
            )}
          </ErrorBoundary>
        </div>
      )}

      {phase === 'mappings' && hasMappingsUi(mappingsConfig) ? (
        <ErrorBoundary>
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={mappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={mappingsConfirmButton} secondaryButton={mappingsCancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};

const SmsTemplateFlowEditor = ({
  title,
  role = 'templateInsert',
  initialFormData,
  editSelection,
  lockedTopic,
  onExit,
}: Omit<TemplateFlowEditorProps, 'channel'>) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');

  const {
    prepareResult,
    prepareError,
    isPreparePending,
    reloadPrepare,
    createInstance,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  } = useSmsConnectorFlowRunner(role, {
    audienceId,
    lang: currentLanguage,
  });

  const insertPrefill = useMemo(
    () => (role === 'templateInsert' ? withStrippedPrefill(initialFormData, lockedTopic) : undefined),
    [initialFormData, lockedTopic, role]
  );
  const resolveStepAutoFill = useMemo(
    () =>
      withLockedTopicNameStrip(
        role === 'templateEdit' || role === 'templateDelete' ? getEditFlowStepHandler('sms', editSelection) : undefined,
        lockedTopic
      ),
    [editSelection, lockedTopic, role]
  );
  const { createTemplate, isSubmitting } = useCreateTemplateHandler(createInstance, onExit, role);
  const lockParams = useLockedTopicSubmit(lockedTopic);

  const [phase, setPhase] = useState<Phase>('form');
  const [formData, setFormData] = useState<FlowFormData>({});
  const [mappingsConfig, setMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);

  const finishWithParams = (flowParams: FlowFormData, config: FlowMappingsConfig | null) => {
    const displayParams = forEditorDisplay(flowParams, lockedTopic);
    setFormData(displayParams);
    setMappingsConfig(config);
    setMappingData({});
    setIsMappingsFormValid(false);
    if (hasMappingsUi(config)) {
      setPhase('mappings');
      return;
    }
    void createTemplate(lockParams(displayParams), {});
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const mappingsConfirmButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void createTemplate(lockParams(formData), mappingData),
    disabled: !isMappingsFormValid || isSubmitting,
  };
  const mappingsCancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => onExit(false),
  };

  const isConnectorReady = !!connectorInstanceId && !!connectorFlowId;
  const isEditorLoading = isAudienceLoading || isChannelLoading || (hasAudience && isPreparePending);
  const connectorUnavailable = !isChannelLoading && !isConnectorReady;

  return (
    <div className='space-y-4'>
      <button type='button' onClick={() => onExit(false)} className='text-sm font-semibold text-brand-700 hover:underline'>
        ← {t('modelli.editor.backToList')}
      </button>
      <h2 className='text-2xl font-bold text-primary-900'>{title}</h2>

      {isAudienceLoading ? (
        <Loading />
      ) : isAudienceSettled && !hasAudience ? (
        <AudienceRequiredEmptyState />
      ) : connectorUnavailable ? (
        <p className='text-sm text-tertiary-600'>{t('modelli.editor.connectorUnavailable')}</p>
      ) : (
        <div className={phase === 'form' ? undefined : 'hidden'}>
          <ErrorBoundary>
            {prepareResult ? (
              <MultiStepFlowForm
                key={`${connectorFlowId}-${audienceId}`}
                id='comuni-template-flow-sms'
                showTemplatePreview
                templatePreviewVariant='sms'
                audienceId={audienceId!}
                connectorInstanceId={connectorInstanceId!}
                connectorFlowId={connectorFlowId!}
                paramsConfig={prepareResult}
                initialFormData={insertPrefill}
                resolveStepAutoFill={resolveStepAutoFill}
                isInAction={isSubmitting}
                onComplete={(allParams, config) => finishWithParams(allParams, config)}
              />
            ) : (
              <div className='flex flex-col items-start gap-3'>
                <p className='text-sm text-tertiary-600'>
                  {isEditorLoading && !prepareError
                    ? tCampaign('smsWorkspace.templates.templateLoadingMessage')
                    : tCampaign('smsWorkspace.templates.failedToLoadTemplate')}
                </p>
                {prepareError ? (
                  <Button intent='secondary' onClick={() => reloadPrepare()} text={tCampaign('smsWorkspace.templates.tryAgain')} />
                ) : null}
              </div>
            )}
          </ErrorBoundary>
        </div>
      )}

      {phase === 'mappings' && hasMappingsUi(mappingsConfig) ? (
        <ErrorBoundary>
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={mappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={mappingsConfirmButton} secondaryButton={mappingsCancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};

const EMAIL_IMAGE_ACCEPT = ['image/png', 'image/jpeg', 'image/gif'];

const EmailTemplateFlowEditor = ({
  title,
  role = 'templateInsert',
  initialFormData,
  editSelection,
  lockedTopic,
  onExit,
}: Omit<TemplateFlowEditorProps, 'channel'>) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const { upload: uploadTemplateImage } = useTemplateImageUpload();

  const {
    prepareResult,
    prepareError,
    isPreparePending,
    reloadPrepare,
    createInstance,
    connectorFlowId,
    connectorInstanceId,
    isChannelLoading,
  } = useEmailConnectorFlowRunner(role, {
    audienceId,
    lang: currentLanguage,
  });

  const insertPrefill = useMemo(
    () => (role === 'templateInsert' ? withStrippedPrefill(initialFormData, lockedTopic) : undefined),
    [initialFormData, lockedTopic, role]
  );
  const resolveStepAutoFill = useMemo(
    () =>
      withLockedTopicNameStrip(
        role === 'templateEdit' || role === 'templateDelete' ? getEditFlowStepHandler('email', editSelection) : undefined,
        lockedTopic
      ),
    [editSelection, lockedTopic, role]
  );
  const { createTemplate, isSubmitting } = useCreateTemplateHandler(createInstance, onExit, role);
  const lockParams = useLockedTopicSubmit(lockedTopic);
  const usesEmailDesigner = role === 'templateInsert' || role === 'templateEdit';

  const [phase, setPhase] = useState<Phase>('form');
  const [formData, setFormData] = useState<FlowFormData>({});
  const [bodyHtml, setBodyHtml] = useState('');
  const [mappingsConfig, setMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [previewFormData, setPreviewFormData] = useState<EmailTemplateFormData | null>(null);

  const finishWithParams = (flowParams: FlowFormData, config: FlowMappingsConfig | null) => {
    const displayParams = forEditorDisplay(flowParams, lockedTopic);
    setFormData(displayParams);
    setMappingsConfig(config);
    setMappingData({});
    setIsMappingsFormValid(false);
    if (hasMappingsUi(config)) {
      setPhase('mappings');
      return;
    }
    void createTemplate(lockParams(displayParams), {});
  };

  const handleFlowComplete = (allParams: FlowFormData, config: FlowMappingsConfig) => {
    // Insert/Edit open the HTML designer; Delete submits the connector flow directly.
    if (!usesEmailDesigner) {
      finishWithParams(allParams, config);
      return;
    }
    const displayParams = forEditorDisplay(allParams, lockedTopic);
    setFormData(displayParams);
    setMappingsConfig(config);
    setBodyHtml(typeof displayParams['template_body_html'] === 'string' ? displayParams['template_body_html'] : '');
    setPhase('design');
  };

  const handleEditorSave = ({ name, html }: { name: string; html: string }) => {
    if (!html.trim()) {
      appDispatch(showToast({ type: ToastType.ERROR, title: String(tCampaign('emailWorkspace.templates.emailPreviewBodyPlaceholder')) }));
      return;
    }
    const nextFormData = { ...formData, template_name: name, template_body_html: html };
    setBodyHtml(html);
    finishWithParams(nextFormData, mappingsConfig);
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const mappingsConfirmButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () => void createTemplate(lockParams(formData), mappingData),
    disabled: !isMappingsFormValid || isSubmitting,
  };
  const mappingsCancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => onExit(false),
  };

  const isConnectorReady = !!connectorInstanceId && !!connectorFlowId;
  const isEditorLoading = isAudienceLoading || isChannelLoading || (hasAudience && isPreparePending);
  const connectorUnavailable = !isChannelLoading && !isConnectorReady;

  if (phase === 'design') {
    return (
      <>
        <HtmlEditorPortal>
          <HtmlEditor
            templateName={stripTopicPrefix(String(formData['template_name'] ?? ''))}
            value={bodyHtml}
            onChange={setBodyHtml}
            onTemplateNameChange={(name) =>
              setFormData((prev) => ({
                ...prev,
                template_name: lockedTopic ? stripTopicPrefix(name) : name,
              }))
            }
            onClose={() => setPhase('form')}
            onPreview={(html) => setPreviewFormData({ ...(formData as EmailTemplateFormData), template_body_html: html })}
            onSave={handleEditorSave}
            onUploadImage={(file) => uploadTemplateImage(file, EMAIL_IMAGE_ACCEPT)}
          />
        </HtmlEditorPortal>
        {previewFormData ? <EmailTemplatePreviewModal formData={previewFormData} onClose={() => setPreviewFormData(null)} /> : null}
      </>
    );
  }

  return (
    <div className='space-y-4'>
      <button type='button' onClick={() => onExit(false)} className='text-sm font-semibold text-brand-700 hover:underline'>
        ← {t('modelli.editor.backToList')}
      </button>
      <h2 className='text-2xl font-bold text-primary-900'>{title}</h2>

      {isAudienceLoading ? (
        <Loading />
      ) : isAudienceSettled && !hasAudience ? (
        <AudienceRequiredEmptyState />
      ) : connectorUnavailable ? (
        <p className='text-sm text-tertiary-600'>{t('modelli.editor.connectorUnavailable')}</p>
      ) : (
        <div className={phase === 'form' ? undefined : 'hidden'}>
          <ErrorBoundary>
            {prepareResult ? (
              <MultiStepFlowForm
                key={`${connectorFlowId}-${audienceId}`}
                id='comuni-template-flow-email'
                showTemplatePreview
                templatePreviewVariant='email'
                htmlBodyFieldMode={usesEmailDesigner ? 'external' : 'default'}
                lastStepActionLabel={
                  usesEmailDesigner ? String(tCampaign('emailWorkspace.templates.continueToEmailDesigner')) : undefined
                }
                audienceId={audienceId!}
                connectorInstanceId={connectorInstanceId!}
                connectorFlowId={connectorFlowId!}
                paramsConfig={prepareResult}
                initialFormData={insertPrefill}
                resolveStepAutoFill={resolveStepAutoFill}
                isInAction={isSubmitting}
                onComplete={handleFlowComplete}
              />
            ) : (
              <div className='flex flex-col items-start gap-3'>
                <p className='text-sm text-tertiary-600'>
                  {isEditorLoading && !prepareError
                    ? tCampaign('smsWorkspace.templates.templateLoadingMessage')
                    : tCampaign('smsWorkspace.templates.failedToLoadTemplate')}
                </p>
                {prepareError ? (
                  <Button intent='secondary' onClick={() => reloadPrepare()} text={tCampaign('smsWorkspace.templates.tryAgain')} />
                ) : null}
              </div>
            )}
          </ErrorBoundary>
        </div>
      )}

      {phase === 'mappings' && hasMappingsUi(mappingsConfig) ? (
        <ErrorBoundary>
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={mappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={mappingsConfirmButton} secondaryButton={mappingsCancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};

export const TemplateFlowEditor = (props: TemplateFlowEditorProps) => {
  if (props.channel === 'whatsapp') return <WhatsappTemplateFlowEditor {...props} />;
  if (props.channel === 'sms') return <SmsTemplateFlowEditor {...props} />;
  return <EmailTemplateFlowEditor {...props} />;
};
